import {createSlice} from '@reduxjs/toolkit'

import fetchPayment from '../thunks/fetchPayment'
import processPostCartUpsell from '../thunks/processPostCartUpsell'
import fetchOrder from '../thunks/fetchOrder'
import validateDiscount from '../thunks/validateDiscount'
import processOrder from '../thunks/processOrder'
import addOrder from '../thunks/addOrder'
import generateTestInfo from '../thunks/generateTestInfo'
import processPaypalOrder from '../thunks/processPaypalOrder'

const initialState = {
  data: {},
  status: 'idle',
  error: null
}

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // Change a single value of the order
    changeOrder(state, action) {
      const {property, value} = action.payload

      if(state.data.hasOwnProperty(property) || property === 'route_override' || property === 'only_gifts') {
        state.data[property] = value
      }
    },
    // Change multiple values at once
    bulkChangeOrder(state, action) {
      if(action.payload && action.payload.length > 0) {
        action.payload.map(({property, value}) => {
          state.data[property] = value
        })
      }
    }
  },
  extraReducers: {
    // Fetch the order from the server
    [fetchOrder.pending]: (state) => {
      state.status = 'loading'
    },
    [fetchOrder.fulfilled]: (state, action) => {
      state.status = 'succeeded'
      state.data = action.payload
    },
    [fetchOrder.rejected]: (state, action) => {
      state.status = 'failed'
      if(action.payload) {
        state.error = action.payload.message
      } else {
        state.error = action.error.message
      }
    },
    // Update order_id on fetching payment data
    [fetchPayment.fulfilled]: (state, action) => {
      if(!state.data || !state.data.order_id) {
        state.data.order_id = action.payload.order_id
      }
    },
    // After adding an order, update the order_id and redirect to payment page
    [addOrder.fulfilled]: (state, action) => {
      state.status = ''

      if(action.payload.order_id !== state.data.order_id) {
        state.data.order_id = action.payload.order_id
      }

      delete state.data.route_override

      if(action.payload.customers_address_shipping_id) {
        state.data.route = '/payment'
      }

      delete state.data.offers
    },
    // Reset customer and address IDs after generating test information
    [generateTestInfo.fulfilled]: (state) => {
      state.data.customer_id = ''
      state.data.customers_address_billing_id = ''
      state.data.customers_address_shipping_id = ''
      state.data.order_id = ''
    },
    // Apply discounts to offers after validation
    [validateDiscount.fulfilled]: (state, action) => {
      if(action.payload.hasOwnProperty('offers')) {
        state.data.offers = action.payload.offers
      } else if(action.payload.hasOwnProperty('gift_card')) {
        if(state.data.hasOwnProperty('gift_cards') && typeof state.data.gift_cards.find(({gift_card_code}) => gift_card_code === action.payload.gift_card.gift_card_code) === 'undefined') {
          state.data.gift_cards.push(action.payload.gift_card)
        } else {
          state.data.gift_cards = [action.payload.gift_card]
        }
      }

      state.status = 'idle'
    },
    // Remove discounts
    'discounts/removeDiscount': (state, action) => {
      if(state.data.offers) {
        state.data.offers.forEach(cart_offer => {
          if(cart_offer.discount_code === action.payload) {
            cart_offer.discount_code = ''
          }
        })

        state.status = 'idle'
      }
    },
    // Remove a specific gift card
    'discounts/removeGiftCard': (state, action) => {
      state.data.gift_cards = state.data.gift_cards.filter(({gift_card_code}) => {
        return gift_card_code !== action.payload
      })

      state.status = 'idle'
    },
    // Clear offers to fetch new ones after processing upsell
    [processPostCartUpsell.fulfilled]: (state) => {
      if(state.data.offers) {
        state.data.offers = null
      }

      state.status = 'idle'
    },
    // Redirect based on whether we have upsells or not
    [processOrder.fulfilled]: (state, action) => {
      if(action.payload.data.upsells) {
        state.data.route = '/upsells'
      } else {
        state.data.route = '/transaction'
      }
    },
    [processPaypalOrder.fulfilled]: (state, action) => {
      if(action.payload.data.upsells) {
        state.data.route = '/upsells'
      } else {
        state.data.route = '/transaction'
      }
    },
    // If editing customer info, reroute to home page and clear order, customer, and address IDs
    'customerInfo/changeCustomerInfoStatus': (state, action) => {
      if(action.payload === 'valid' || action.payload === 'valid-email') {
        state.data.customer_id = ''
        state.data.customers_address_billing_id = ''
        state.data.customers_address_shipping_id = ''
      }
    },
    'customerInfo/shipToBill': (state) => {
      state.data.customers_address_billing_id = state.data.customers_address_shipping_id
    },
    'customerInfo/resetBill': (state) => {
      state.data.customers_address_billing_id = ''
    },
    'customerInfo/resetShip': (state) => {
      state.data.customers_address_shipping_id = ''
    },
    // Add in-cart upsell
    'cart/addInCartUpsell': (state, action) => {
      if(state.data.offers) {
        state.data.offers.splice(action.payload.index + 1, 0, {
          ...action.payload.offer,
          campaign_id: state.data.campaign_id,
          order_offer_upsell: null
        })
      }
    },
    // Remove in-cart upsell
    'cart/removeInCartUpsell': (state, action) => {
      if(state.data.offers) {
        state.data.offers = state.data.offers.filter(({id}) => id !== action.payload)
      }
    },
    'cart/removeOrderOfferByID': (state, action) => {
      if(state.data.offers) {
        state.data.offers = state.data.offers.filter(({order_offer_id}) => order_offer_id !== action.payload)
      }
    },
    'cart/removeOfferByKey': (state, action) => {
      if(state.data.offers) {
        state.data.offers = state.data.offers.filter((offer, index) => index !== action.payload)
      }
    }
  }
})

export default orderSlice.reducer

export const {
    changeOrder,
    bulkChangeOrder
  } = orderSlice.actions


// WEBPACK FOOTER //
// ./client/resources/js/checkout/redux/slices/orderSlice.js